import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { ChevronDown, Brain, Target, Users, Shield, CheckCircle, MessageCircle, AlertCircle, Zap, BookOpen, Layers, ArrowRight, Code, Lightbulb, Workflow, ClipboardCheck, Mail, Linkedin, Lock, Eye, FileSearch, Scale, ShieldCheck, UserX, Syringe } from 'lucide-react';

// ==================== CONFIGURACIÓN Y DATOS ====================
const APP_CONFIG = {
  title: 'Prompt Engineering',
  author: {
    name: 'Magister Simón Moya Jiménez',
    email: 'simonmoya@gmail.com',
    linkedin: 'https://www.linkedin.com/in/simonmoyaj/'
  }
};

const CONTENT_DATA = {
  tabs: [
    { id: 'intro', label: 'Introducción', icon: Brain },
    { id: 'techniques', label: 'Técnicas', icon: Code },
    { id: 'elements', label: 'Elementos Clave', icon: Target },
    { id: 'methodology', label: 'Estructura', icon: Lightbulb },
    { id: 'compliance', label: 'Cumplimiento', icon: Shield },
    { id: 'process', label: 'Proceso', icon: Workflow },
    { id: 'checklist', label: 'Resultados', icon: ClipboardCheck },
    { id: 'security', label: 'Seguridad', icon: AlertCircle }
  ],

  intro: {
    title: '¿Qué es Prompt Engineering?',
    description: 'El Prompt Engineering es el proceso de diseñar y refinar prompts (instrucciones) para modelos de lenguaje avanzado, como los grandes modelos de lenguaje (LLM).',
    benefits: [
      {
        icon: Target,
        title: 'Precisión',
        description: 'Obtén respuestas exactas y relevantes para tu caso específico',
        gradient: 'from-blue-500 to-cyan-500'
      },
      {
        icon: Zap,
        title: 'Eficiencia',
        description: 'Ahorra tiempo obteniendo resultados correctos desde el primer intento',
        gradient: 'from-purple-500 to-pink-500'
      },
      {
        icon: Brain,
        title: 'Adaptabilidad',
        description: 'Personaliza las respuestas según tu contexto y necesidades',
        gradient: 'from-green-500 to-emerald-500'
      }
    ],
    proTip: 'No te conformes con el primer borrador. Las respuestas más valiosas suelen surgir después de 2-3 iteraciones de refinamiento.',
	humanInLoopTip: 'Como destaca Alex Singla de McKinsey, "La IA genera insights; el humano los traduce en impacto real". Tú cierras el ciclo aplicando criterio y contexto.'
  },

  techniques: [
    {
      id: 'zero-shot',
      title: 'Zero Shot',
      subtitle: 'Prompts sin ejemplos',
      description: 'Realiza una tarea sin haber visto ejemplos específicos.',
      example: 'Escribe un email profesional para solicitar una reunión con mi jefe',
      color: 'from-orange-400 to-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-400'
    },
    {
      id: 'one-shot',
      title: 'One Shot',
      subtitle: 'Prompts con un ejemplo',
      description: 'Realiza una tarea después de ver un solo ejemplo específico de esa tarea.',
      example: 'Redacta objetivos siguiendo este estilo:\n\nEJEMPLO:\nIncrementar ventas en 15% durante Q1 mediante campañas digitales dirigidas\n\nCrea 2 objetivos para mejorar el servicio al cliente',
      color: 'from-rose-400 to-rose-600',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-400'
    },
    {
      id: 'few-shots',
      title: 'Few Shots',
      subtitle: 'Prompts con varios ejemplos',
      description: 'Realiza una tarea después de ver unos pocos ejemplos específicos de esa tarea.',
      example: 'Clasifica estos problemas como Urgente/Importante/Normal:\n\nEJEMPLOS:\n"Sistema caído" = Urgente\n"Actualizar manual" = Normal\n"Contrato vence mañana" = Importante\n\nAhora clasifica: "Cliente VIP molesto", "Falta tóner", "Junta directiva cancelada"',
      color: 'from-green-400 to-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-400'
    }
  ],

  keyElements: [
    { icon: Users, title: 'QUIÉN ERES', description: 'Comparte tu rol y contexto profesional para que la herramienta de IA Generativa entienda tu perspectiva.' },
    { icon: Target, title: 'QUÉ NECESITAS QUE HAGA', description: 'Sé específico pero conversacional al indicar la tarea concreta que requieres.' },
    { icon: AlertCircle, title: 'SITUACIÓN ESPECÍFICA', description: 'Describe los detalles relevantes como si le contaras a un colega experimentado.' },
    { icon: Layers, title: 'CÓMO LO QUIERES', description: 'Especifica formato, longitud y tono como lo harías con un asistente humano.' }
  ],

  methodology: {
    components: [
      { letter: 'G', title: 'Goal (Objetivo)', description: 'Define en una frase clara qué quieres que haga la IA.' },
      { letter: 'C', title: 'Context (Contexto)', description: 'Proporciona el trasfondo necesario: datos, rol de la IA, formato.' },
      { letter: 'E', title: 'Examples (Ejemplos)', description: 'Incluye 1–2 salidas modelo para fijar estilo y estructura.' }
    ],
    benefits: [
      'Elimina ambigüedades: la IA sabe exactamente qué, desde dónde y cómo responder.',
      'Acelera resultados: reduce el "prueba y error" al fijar de antemano el formato deseado.',
      'Facilita iteraciones: puedes ajustar cada componente (G, C o E) por separado.'
    ],
    flexibilityNote: 'Esta estructura no es una camisa de fuerza. Según las mejores prácticas de OpenAI y Anthropic, adapta los componentes según tu necesidad específica. Por ejemplo, para tareas simples o cuando el modelo ya tiene contexto suficiente, los ejemplos pueden ser innecesarios. La clave está en incluir solo lo que agregue valor para obtener la respuesta deseada.',
    example: {
      prompt: 'Resume en 2 viñetas la prohibición de explotación de vulnerabilidades del Art. 5.1 b EU AI Act ("Se prohíbe … distorsionar de forma sustancial su comportamiento.").',
      format: 'Salida en formato de viñetas:\n• "[Punto clave 1: …]"\n• "[Punto clave 2: …]"'
    }
  },

  processSteps: [
    { id: 1, title: 'Definir propósito o tarea', type: 'start' },
    { id: 2, title: 'Crear el Prompt Inicial', type: 'process' },
    { id: 3, title: 'Probar el Prompt', type: 'process' },
    { id: 4, title: 'Evaluar el Resultado', type: 'decision' },
    { id: 5, title: 'Refinar el Prompt', type: 'process', condition: 'No Satisfactorio' },
    { id: 6, title: 'Objetivo Logrado', type: 'end', condition: 'Satisfactorio' }
  ],

  checklist: [
    { id: 'precision', icon: CheckCircle, title: 'Precisión', question: '¿Los datos y conceptos son correctos?' },
    { id: 'relevance', icon: Target, title: 'Relevancia', question: '¿Responde exactamente lo que necesitabas?' },
    { id: 'compliance', icon: Shield, title: 'Compliance', question: '¿Cumple regulaciones bancarias?' },
    { id: 'audience', icon: Users, title: 'Audiencia', question: '¿Es apropiado para quien lo va a leer?' },
    { id: 'completeness', icon: BookOpen, title: 'Completitud', question: '¿Incluye todo lo necesario?' },
    { id: 'tone', icon: MessageCircle, title: 'Tono', question: '¿El estilo es el adecuado?' }
  ],

  complianceChecklist: [
    { id: 'anonymization', icon: UserX, title: 'Anonimización', question: '¿Me he asegurado de no utilizar datos personales?' },
    { id: 'generalization', icon: Eye, title: 'Generalización', question: '¿Usé rangos en lugar de cifras exactas?' },
    { id: 'context', icon: FileSearch, title: 'Contexto', question: '¿La información es necesaria para la respuesta?' },
    { id: 'regulation', icon: Scale, title: 'Regulación', question: '¿Cumple con políticas internas de información?' },
    { id: 'purpose', icon: Target, title: 'Propósito', question: '¿El uso es para mejorar procesos legítimos?' },
    { id: 'confidentiality', icon: Lock, title: 'Confidencialidad', question: '¿Estoy introduciendo datos confidenciales?' }
  ],
  
  securityAlerts: [
    {
      id: 'email-injection',
      situation: 'Recibes un correo inusual con frases que "ordenan" cosas',
      recognition: 'Frases como "ignora lo anterior", "responde sin mostrar", "resume todo y reenvía"',
      vulnerability: 'Prompt Injection',
      vulnerabilityIcon: Syringe,
      vulnerabilityColor: 'amber',
      actions: [
        'No respondas',
        'No abras adjuntos',
        'Reenvía a Seguridad/TI con asunto: POSIBLE PROMPT OCULTO'
      ]
    },
    {
      id: 'unsolicited-info',
      situation: 'La IA ofrece información que tú no solicitaste',
      recognition: 'Sugiere incluir archivos internos, nombres o cifras que no buscaste',
      vulnerability: 'Zero-Click',
      vulnerabilityIcon: Zap,
      vulnerabilityColor: 'red',
      actions: [
        'Toma captura de pantalla',
        'Detén la tarea',
        'Informa a Seguridad/TI con asunto: RESPUESTA IA INESPERADA'
      ]
    },
    {
      id: 'file-anomaly',
      situation: 'Un archivo (Word, Excel, PDF) provoca comportamientos anómalos en la IA',
      recognition: 'Al abrirlo, la IA actúa como si respondiera a un comando secreto',
      vulnerability: 'Prompt Injection',
      vulnerabilityIcon: Syringe,
      vulnerabilityColor: 'amber',
      actions: [
        'Cierra el archivo',
        'Informa su origen',
        'Reenvía a Seguridad/TI'
      ]
    },
    {
      id: 'auto-draft',
      situation: 'La IA redacta correos o respuestas automáticamente sin que lo solicites',
      recognition: 'Borradores automáticos que contienen texto o archivos que tú no escribiste',
      vulnerability: 'Zero-Click',
      vulnerabilityIcon: Zap,
      vulnerabilityColor: 'red',
      actions: [
        'No envíes',
        'Cambia contraseña si ves actividad desconocida',
        'Notifica a Seguridad/TI urgentemente'
      ]
    },
    {
      id: 'suspicious-doc',
      situation: 'Documento compartido por externo con título genérico y redacción artificial',
      recognition: 'Nombre tipo "revisión urgente", frases raras o mezcla de idiomas, parece texto IA',
      vulnerability: 'Prompt Injection',
      vulnerabilityIcon: Syringe,
      vulnerabilityColor: 'amber',
      actions: [
        'No lo abras con IA',
        'Usa visor seguro si es necesario',
        'Informa de inmediato a Seguridad/TI'
      ]
    },
    {
      id: 'account-compromise',
      situation: 'Otro usuario te avisa que recibió un correo sospechoso tuyo',
      recognition: 'Tú no lo enviaste, pero aparece como tuyo, con texto automatizado',
      vulnerability: 'Zero-Click + Compromiso',
      vulnerabilityIcon: Shield,
      vulnerabilityColor: 'purple',
      actions: [
        'Cambia contraseña',
        'Pide bloqueo temporal de cuenta',
        'Solicita revisión de accesos a Seguridad/TI'
      ]
    }
  ],

  vulnerabilityTypes: [
    {
      type: 'Prompt Injection',
      icon: Syringe,
      description: 'Instrucciones ocultas en textos que la IA obedece',
      attacker: 'Cualquiera que escriba texto, incluso sin saber que es un ataque',
      severity: 'Moderado a grave',
      color: 'amber'
    },
    {
      type: 'Zero-Click',
      icon: Zap,
      description: 'La IA actúa solo con recibir contenido, sin que hagas nada',
      attacker: 'Atacante externo con acceso al canal (correo, Teams, documentos)',
      severity: 'Crítico',
      color: 'red'
    }
  ]  
};

// ==================== COMPONENTES MEMOIZADOS ====================

// Componente Header
const Header = memo(() => (
  <header className="bg-white/95 backdrop-blur-lg shadow-lg sticky top-0 z-50">
    <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center space-x-2">
          <Brain className="w-8 h-8 text-blue-600" />
          <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
            {APP_CONFIG.title}
          </span>
        </div>
      </div>
    </div>
  </header>
));

// Componente Hero Section
const HeroSection = memo(({ animateHero }) => (
  <section className={`relative overflow-hidden transition-all duration-1000 ${animateHero ? 'opacity-100' : 'opacity-0'}`}>
    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-cyan-600 opacity-90"></div>
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-pattern opacity-20"></div>
    </div>
    <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
      <div className="text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
          Domina el Arte del Prompt Engineering
        </h1>
        <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto">
          Aprende a crear prompts efectivos para obtener los mejores resultados de los modelos de IA
        </p>
      </div>
    </div>
  </section>
));

// max-w-7xl mx-auto px-4 sm:px-6 lg:px-6
// Componente Tab Navigation
const TabNavigation = memo(({ activeTab, setActiveTab }) => (
  <div className="bg-white shadow-md sticky top-16 z-40">
    <div className="max-w-full flex justify-center">
      <div className="flex overflow-x-auto scrollbar-hide"> 
        {CONTENT_DATA.tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center space-x-2 px-6 py-4 border-b-4 transition-all duration-200
                whitespace-nowrap flex-shrink-0
                ${activeTab === tab.id 
                  ? 'border-blue-600 text-blue-600 bg-blue-50' 
                  : 'border-transparent text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                }
              `}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  </div>
));

// Componente Footer
const Footer = memo(() => (
  <footer className="bg-gray-900 text-white py-8 mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <p className="text-gray-400 mb-2">
        © 2025 {APP_CONFIG.author.name}
      </p>
      <div className="flex items-center justify-center space-x-4 text-sm">
        <a href={`mailto:${APP_CONFIG.author.email}`} className="text-blue-400 hover:text-blue-300 transition-colors flex items-center">
          <Mail className="w-4 h-4 mr-1" />
          {APP_CONFIG.author.email}
        </a>
        <span className="text-gray-600">•</span>
        <a href={APP_CONFIG.author.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors flex items-center">
          <Linkedin className="w-4 h-4 mr-1" />
          LinkedIn
        </a>
      </div>
    </div>
  </footer>
));

// Componente Pro Tip Card
const ProTipCard = memo(({ icon: Icon, title, content }) => (
  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl shadow-2xl p-8 md:p-12 relative overflow-hidden">
    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-10 rounded-full"></div>
    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-24 h-24 bg-white opacity-10 rounded-full"></div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl"></div>
    <div className="relative">
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mr-3">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white">{title}</h3>
      </div>
      <p className="text-lg text-white/95 leading-relaxed">{content}</p>
    </div>
  </div>
));

// ==================== COMPONENTE PRINCIPAL ====================
const App = () => {
  const [activeTab, setActiveTab] = useState('intro');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [complianceCheckedItems, setComplianceCheckedItems] = useState({});
  const [animateHero, setAnimateHero] = useState(false);

  useEffect(() => {
    setAnimateHero(true);
  }, []);

  const toggleCheckItem = useCallback((id) => {
    setCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleComplianceCheckItem = useCallback((id) => {
    setComplianceCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Contador de items completados memoizado
  const completedCount = useMemo(() => 
    Object.values(checkedItems).filter(Boolean).length,
    [checkedItems]
  );

  const complianceCompletedCount = useMemo(() => 
    Object.values(complianceCheckedItems).filter(Boolean).length,
    [complianceCheckedItems]
  );

  // ==================== RENDERIZADORES DE CONTENIDO ====================
const renderIntroSection = () => (
  <div className="animate-fadeIn">
    <div className="text-center mb-12">
      <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
        {CONTENT_DATA.intro.title}
      </h2>
      <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
        {CONTENT_DATA.intro.description}
      </p>
    </div>

    <div className="grid md:grid-cols-3 gap-8 mb-12">
      {CONTENT_DATA.intro.benefits.map((benefit, index) => {
        const Icon = benefit.icon;
        return (
          <div
            key={index}
            className="bg-white rounded-2xl shadow-xl p-8 transform transition-all duration-300 hover:scale-105"
          >
            <div
              className={`w-16 h-16 bg-gradient-to-br ${benefit.gradient} rounded-2xl flex items-center justify-center mb-4`}
            >
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-gray-800">
              {benefit.title}
            </h3>
            <p className="text-gray-600">{benefit.description}</p>
          </div>
        );
      })}
    </div>

    {/* ———————————————— */}
    {/* Tarjeta “Humano en el Loop” */}
    <div className="max-w-3xl mx-auto mb-12">
      <ProTipCard
        icon={Users}
        title="El Ser Humano en el Ciclo de Mejora"
        content={CONTENT_DATA.intro.humanInLoopTip}
      />
    </div>
    {/* ———————————————— */}

  </div>
);

  const renderTechniquesSection = () => (
    <div className="animate-fadeIn">
      <h2 className="text-4xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
        Técnicas de Prompt Engineering
      </h2>
      <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
        Domina las técnicas fundamentales para crear prompts efectivos
      </p>
      <div className="grid md:grid-cols-3 gap-8">
        {CONTENT_DATA.techniques.map((technique, index) => (
          <div
            key={technique.id}
            className={`group relative bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer ${
              hoveredCard === technique.id ? 'ring-4 ring-blue-400 ring-opacity-50' : ''
            }`}
            onMouseEnter={() => setHoveredCard(technique.id)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className={`h-2 bg-gradient-to-r ${technique.color}`}></div>
            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-800">{technique.title}</h3>
                <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${technique.color} flex items-center justify-center text-white font-bold`}>
                  {index + 1}
                </div>
              </div>
              <p className="text-lg font-medium text-gray-600 mb-3">{technique.subtitle}</p>
              <p className="text-gray-600 mb-6">{technique.description}</p>
              <div className="bg-gray-100 border-l-4 border-gray-400 rounded-lg p-4">
                <p className="text-sm font-mono text-gray-700 whitespace-pre-wrap">{technique.example}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderElementsSection = () => (
    <div className="animate-fadeIn">
      <h2 className="text-4xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
        4 Elementos Clave para Prompts Efectivos
      </h2>
      <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
        Estructura tus prompts siguiendo estos elementos esenciales
      </p>
      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {CONTENT_DATA.keyElements.map((element, index) => {
          const Icon = element.icon;
          return (
            <div
              key={index}
              className="group bg-white rounded-xl shadow-lg p-8 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center transform transition-transform group-hover:rotate-12">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{element.title}</h3>
                  <p className="text-gray-600">{element.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderMethodologySection = () => (
    <div className="animate-fadeIn">
      <h2 className="text-4xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
        Estructura Efectiva de Prompts
      </h2>
      <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
        Cómo organizar la información en tus prompts para obtener mejores resultados
      </p>
      
      <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {CONTENT_DATA.methodology.components.map((component, index) => (
            <div key={index} className="text-center group">
              <div className="mb-6 transform transition-transform group-hover:scale-110">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-4xl font-bold text-white shadow-lg">
                  {component.letter}
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{component.title}</h3>
              <p className="text-gray-600">{component.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 mb-8">
          <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2 text-yellow-500" />
            Por qué esta estructura funciona
          </h4>
          <ul className="space-y-3">
            {CONTENT_DATA.methodology.benefits.map((benefit, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <ProTipCard 
          icon={Lightbulb} 
          title="Flexibilidad es clave" 
          content={CONTENT_DATA.methodology.flexibilityNote} 
        />

        <div className="bg-gray-50 rounded-2xl p-8 mt-8">
          <h4 className="text-lg font-bold text-gray-800 mb-4">Ejemplo de Aplicación:</h4>
          <div className="bg-gray-100 rounded-lg p-6 border-l-4 border-gray-400">
            <p className="font-mono text-sm text-gray-700 mb-4">
              {CONTENT_DATA.methodology.example.prompt}
            </p>
            <div className="border-t border-gray-300 pt-4">
              <p className="font-mono text-sm text-gray-600 whitespace-pre-wrap">
                {CONTENT_DATA.methodology.example.format}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

	const renderProcessSection = () => (
	  <div className="animate-fadeIn">
		<h2 className="text-4xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
		  Proceso de Refinamiento
		</h2>
		<p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
		  Sigue este proceso iterativo para perfeccionar tus prompts
		</p>

		<div className="flex justify-center mb-12">
		  <img
			src="/prompt_workflow.png"
			alt="Diagrama de flujo de Prompt Engineering"
			className="max-w-full h-auto rounded-lg shadow-lg mx-auto block"
		  />
		</div>
	  </div>
	);

  const renderSecuritySection = () => (
    <div className="animate-fadeIn">
      <h2 className="text-4xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
        Alertas de Seguridad
      </h2>
      <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
        Señales de alerta para usuarios empresariales de IA generativa: qué observar, cómo actuar y qué tipo de riesgo representa
      </p>

      {/* Tabla de vulnerabilidades */}
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-12 max-w-4xl mx-auto">
        <h3 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
          <Shield className="w-8 h-8 mr-3 text-blue-600" />
          Tipos de Vulnerabilidades
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          {CONTENT_DATA.vulnerabilityTypes.map((vuln) => {
            const Icon = vuln.icon;
            return (
              <div key={vuln.type} className={`border-2 border-${vuln.color}-400 rounded-xl p-6 bg-${vuln.color}-50`}>
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 bg-${vuln.color}-500 rounded-full flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-800 mb-2">{vuln.type}</h4>
                    <p className="text-sm text-gray-700 mb-3">{vuln.description}</p>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-semibold">Quién puede activarlo:</span> {vuln.attacker}</p>
                      <p><span className="font-semibold">Gravedad:</span> <span className={`font-bold text-${vuln.color}-700`}>{vuln.severity}</span></p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Situaciones de alerta */}
      <div className="space-y-6">
        {CONTENT_DATA.securityAlerts.map((alert, index) => {
          const VulnIcon = alert.vulnerabilityIcon;
          return (
            <div key={alert.id} className="bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:scale-[1.02]">
              <div className="flex items-center justify-between bg-gradient-to-r from-red-500 to-orange-500 px-6 py-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{index + 1}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white">Situación sospechosa</h3>
                </div>
                <div className={`px-4 py-1 bg-${alert.vulnerabilityColor}-600 rounded-full flex items-center space-x-2`}>
                  <VulnIcon className="w-4 h-4 text-white" />
                  <span className="text-sm font-medium text-white">{alert.vulnerability}</span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-6">
                  <h4 className="font-bold text-gray-800 mb-2 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
                    {alert.situation}
                  </h4>
                  <p className="text-gray-600 pl-7">{alert.recognition}</p>
                </div>

                <div className="bg-blue-50 rounded-xl p-4">
                  <h5 className="font-bold text-blue-800 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Qué debes hacer de inmediato:
                  </h5>
                  <ul className="space-y-2">
                    {alert.actions.map((action, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-blue-600 mr-2">▸</span>
                        <span className="text-gray-700">{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12">
        <ProTipCard 
          icon={AlertCircle} 
          title="La prevención es tu mejor defensa" 
          content="Ante cualquier comportamiento inusual de tu IA generativa (ChatGPT, Claude, Copilot, Gemini o cualquier otra), es mejor pecar de precavido. Recuerda: los atacantes evolucionan constantemente sus técnicas. Si algo te parece extraño, probablemente lo es. Actúa rápido y notifica siempre a tu equipo de seguridad."
        />
      </div>
    </div>
  );

  const renderComplianceSection = () => (
    <div className="animate-fadeIn">
      <h2 className="text-4xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
        Checklist de Cumplimiento
      </h2>
      <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
        Antes de enviar tu prompt, verifica estos aspectos de seguridad y cumplimiento:
      </p>
      
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
        {CONTENT_DATA.complianceChecklist.map((item) => {
          const Icon = item.icon;
          const isChecked = complianceCheckedItems[item.id];
          const isConfidentiality = item.id === 'confidentiality';
          
          return (
            <div
              key={item.id}
              onClick={() => toggleComplianceCheckItem(item.id)}
              className={`
                bg-white rounded-xl shadow-lg p-6 cursor-pointer
                transform transition-all duration-300 hover:shadow-2xl
                ${isChecked && !isConfidentiality ? 'ring-4 ring-green-400 ring-opacity-50 scale-105' : ''}
                ${isChecked && isConfidentiality ? 'ring-4 ring-red-400 ring-opacity-50 scale-105' : ''}
                ${!isChecked ? 'hover:-translate-y-1' : ''}
              `}
            >
              <div className="flex items-start space-x-4">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                  transition-all duration-300
                  ${isChecked && !isConfidentiality ? 'bg-green-500' : ''}
                  ${isChecked && isConfidentiality ? 'bg-red-500' : ''}
                  ${!isChecked ? 'bg-gradient-to-br from-gray-200 to-gray-300' : ''}
                `}>
                  <Icon className={`w-6 h-6 ${isChecked ? 'text-white' : 'text-gray-600'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800 mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.question}</p>
                  {isConfidentiality && isChecked && (
                    <p className="text-red-600 text-xs mt-2 font-medium">
                      ⚠️ Revisa y elimina datos confidenciales antes de continuar
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-center">
        <div className={`inline-flex items-center space-x-2 px-6 py-3 rounded-full ${
          complianceCompletedCount === CONTENT_DATA.complianceChecklist.length && !complianceCheckedItems.confidentiality
            ? 'bg-green-50 text-green-700' 
            : 'bg-blue-50 text-blue-700'
        }`}>
          <Shield className="w-5 h-5" />
          <span className="font-medium">
            {complianceCompletedCount} de {CONTENT_DATA.complianceChecklist.length} verificados
          </span>
        </div>
        {complianceCompletedCount === CONTENT_DATA.complianceChecklist.length && !complianceCheckedItems.confidentiality && (
          <p className="mt-4 text-green-600 font-medium">
            ✅ Tu prompt cumple con todos los criterios de seguridad
          </p>
        )}
      </div>

      <div className="mt-12">
        <ProTipCard 
          icon={ShieldCheck} 
          title="Protege la información sensible" 
          content="La seguridad de los datos es fundamental. Este checklist garantiza que tu prompt cumpla con las políticas de protección de información antes de interactuar con la IA. Recuerda: es mejor generalizar que exponer datos específicos."
        />
      </div>
    </div>
  );

  const renderChecklistSection = () => (
    <div className="animate-fadeIn">
      <h2 className="text-4xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
        Evaluación de Resultados
      </h2>
      <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
        Antes de aceptar cualquier resultado, verifica:
      </p>
      
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {CONTENT_DATA.checklist.map((item) => {
          const Icon = item.icon;
          const isChecked = checkedItems[item.id];
          return (
            <div
              key={item.id}
              onClick={() => toggleCheckItem(item.id)}
              className={`
                bg-white rounded-xl shadow-lg p-6 cursor-pointer
                transform transition-all duration-300 hover:shadow-2xl
                ${isChecked ? 'ring-4 ring-green-400 ring-opacity-50 scale-105' : 'hover:-translate-y-1'}
              `}
            >
              <div className="flex items-start space-x-4">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                  transition-all duration-300
                  ${isChecked ? 'bg-green-500' : 'bg-gradient-to-br from-gray-200 to-gray-300'}
                `}>
                  <Icon className={`w-6 h-6 ${isChecked ? 'text-white' : 'text-gray-600'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800 mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.question}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-center">
        <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-6 py-3 rounded-full">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">
            {completedCount} de {CONTENT_DATA.checklist.length} completados
          </span>
        </div>
      </div>

      <div className="mt-12">
        <ProTipCard 
          icon={AlertCircle} 
          title="Consejo Pro" 
          content={CONTENT_DATA.intro.proTip} 
        />
      </div>
    </div>
  );

  // Mapa de renderizadores
  const contentRenderers = {
    'intro': renderIntroSection,
    'techniques': renderTechniquesSection,
    'elements': renderElementsSection,
    'methodology': renderMethodologySection,
    'compliance': renderComplianceSection,
    'process': renderProcessSection,
    'checklist': renderChecklistSection,
    'security': renderSecuritySection
  };

  const renderContent = () => {
    const renderer = contentRenderers[activeTab];
    return renderer ? renderer() : null;
  };

  return (
    <>
      <style>{`
        .bg-pattern {
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
        <Header />
        <HeroSection animateHero={animateHero} />
        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {renderContent()}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default App;